import { Button, Flex, Spin } from "antd";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useUserPermissions } from "~context/UserPermissionsContext";
import type { ImageBaseTypes } from "~enums/imageBaseTypes";
import type { ImageTypes } from "~enums/imageTypes";
import { type PermissionNames, PermissionOperations } from "~enums/permissions";
import type { Image } from "~types/image";
import classNames from "~utils/classNames";
import { ImageUploader } from "./components";
import { useDeleteImage, useUploadImage } from "./hooks";

import "./UploadImageForm.scss";
import ImageView from "~components/ImageView/ImageView.tsx";
import { ImageSizes } from "~enums/imageSizes.ts";
import { findImageByTypes } from "~utilities/findImageByTypes.ts";

type Props = {
  baseType: ImageBaseTypes;
  baseId: string;
  imageType: ImageTypes;
  permissionKey: PermissionNames;
  images: Image[];
  onSuccess?: (image: Image) => void;
};

const PlaceholderImage = () => (
  <svg>
    <line x1="0" y1="100%" x2="100%" y2="0" />
    <line x1="0" y1="0" x2="100%" y2="100%" />
  </svg>
);

const UploadImageForm = ({ baseType, baseId, imageType, permissionKey, images, onSuccess }: Props) => {
  const { hasPermission } = useUserPermissions();
  const [currentImage, setCurrentImage] = useState<Image | undefined>();
  const [preview, setPreview] = useState<string>("");
  const [imageToUpload, setImageToUpload] = useState<File>();
  const { mutateAsync: uploadImage, isPending: isImageUploading } = useUploadImage();
  const { mutateAsync: deleteImage, isPending: isImageClearing } = useDeleteImage();
  const hasPermissionToEditImage = hasPermission(permissionKey, PermissionOperations.UPDATE);
  const isLoading = isImageUploading || isImageClearing;

  useEffect(() => {
    const initialImage = findImageByTypes(images, [imageType]);
    if (initialImage) {
      setCurrentImage(initialImage);
      setPreview(initialImage.url);
    }
  }, [images, imageType]);

  const handleClear = async () => {
    if (imageToUpload) {
      setImageToUpload(undefined);
      setPreview(currentImage ? currentImage.url : "");
    } else if (currentImage) {
      await deleteImage(currentImage.imageId);
      setPreview("");
    }
  };

  const handleDrop = (file) => {
    setImageToUpload(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = () => {
    if (imageToUpload) {
      uploadImage({ baseId, baseType, file: imageToUpload, imageType }).then((response) => {
        setImageToUpload(undefined);
        setCurrentImage(response?.data?.[0]);
        if (onSuccess) {
          onSuccess(response?.data?.[0]);
        }
      });
    }
  };

  const isPreviewAreLocalFile = preview.startsWith("blob:");

  return (
    <div className="upload-form-wrapper">
      <div className={classNames("image-wrapper", isLoading && "is-loading")}>
        {isLoading && <Spin size="large" className="spinner" />}
        <ImageView
          imageType={imageType}
          size={!isPreviewAreLocalFile ? ImageSizes.Raw : undefined}
          imageUrl={preview}
          fallback={<PlaceholderImage />}
        />
      </div>
      {hasPermissionToEditImage && (
        <>
          <ImageUploader handleDrop={handleDrop} disabled={isLoading} />
          <Flex gap={8} justify="flex-end" align="center">
            <Button onClick={handleClear} disabled={!imageToUpload && !preview}>
              <FormattedMessage id="image.clear" />
            </Button>
            <Button type="primary" onClick={handleUpload} disabled={!imageToUpload}>
              <FormattedMessage id="image.upload" />
            </Button>
          </Flex>
        </>
      )}
    </div>
  );
};

export default UploadImageForm;
